import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Subject, lastValueFrom, takeUntil } from 'rxjs';
import Papa from 'papaparse';
import { BackendApiService } from 'src/app/core/services/backend-api';
import {
  PurchaseExecutiveListResponse,
  PurchaseExecutiveReportRow,
  PurchaseExecutivesReportRequest,
  PurchaseExecutiveUser,
} from 'src/app/shared/models/database-models/PurchaseExecutivesReport.model';
import moment, { Moment } from 'moment';
import { AlertType } from 'src/app/shared/components/alert/alert.component';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  DropDownOptions,
  ExtendedKeycloakUser,
  StoreDropdowns,
} from 'src/app/shared/models/app-models';
import { select, Store } from '@ngrx/store';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_DATE_FORMAT } from 'src/app/data/constants/Data';
import { isMPDocumentNumberValid } from '../shared/utils/common-validation';
import { MatCheckboxChange } from '@angular/material/checkbox';

export const INITIAL_CSV_HEADER_KEYS = [
  'MPDocumentNumber',
  'PurchaseType',
  'WorkflowStatus',
  'SecurityClassExt',
  'RequestingOfficerName',
  'RequestingOfficerDivision',
  'SubmittedDate',
  'TaggingCompletionDate',
  'MPItemNumber',
  'MaterialGroup',
  'MaterialSubGroup',
  'ItemDescription',
  'BrandNameOEM',
  'PartModelNumber',
  'Quantity',
  'UnitOfMeasurement',
  'PlannedDeliveryDate',
  'Criticality',
  'PurchaseExecutiveName',
  'VendorResponded',
  'VendorName',
  'RFQNumber',
  'RFQItemNumber',
  'RFQDeadline',
  'ERRecommendedVendor',
  'ERDocumentNumber',
  'ERItemNumber',
  'ERSubmittedDate',
  'ERProcessStatus',
  'ERAcknowledgedDate',
  'ERCompletionDate',
  'RFQSummaryNumber',
  'RFQSummaryStatus',
  'AwardedVendorName',
  'ReasonName',
  'PODocumentNumber',
  'POItemNumber',
  'POSubmittedDate',
  'POProcessStatus',
  'POApprovedDate',
  'POSMLNumber',
] as const;

export type CsvHeaderKey = (typeof INITIAL_CSV_HEADER_KEYS)[number];

export const CSV_HEADER_MAPPING: { [key in CsvHeaderKey]: string } = {
  MPDocumentNumber: 'MP No.',
  PurchaseType: 'Purchase Mode',
  WorkflowStatus: 'Workflow Status',
  SecurityClassExt: 'Security Classification (External)',
  RequestingOfficerName: 'RO Name',
  RequestingOfficerDivision: 'RO Division',
  SubmittedDate: 'Submitted Date',
  TaggingCompletionDate: 'Tagging Completion Date',
  MPItemNumber: 'MP Item No.',
  MaterialGroup: 'Material Group',
  MaterialSubGroup: 'Material Sub Group',
  ItemDescription: 'Item Description',
  BrandNameOEM: 'Brand Name/OEM',
  PartModelNumber: 'Part No./Model No.',
  Quantity: 'Qty',
  UnitOfMeasurement: 'UoM',
  PlannedDeliveryDate: 'Planned Delivery Date',
  Criticality: 'Criticality Code',
  PurchaseExecutiveName: 'Assigned PE',
  VendorResponded: 'Vendor Responded',
  VendorName: 'Vendor Sourced',
  RFQNumber: 'RFQ No.',
  RFQItemNumber: 'RFQ Item No.',
  RFQDeadline: 'RFQ Deadline',
  ERRecommendedVendor: 'Recommended Vendor',
  ERDocumentNumber: 'ER No.',
  ERItemNumber: 'ER Item No.',
  ERSubmittedDate: 'ER Submission Date',
  ERProcessStatus: 'ER Status',
  ERAcknowledgedDate: 'ER Acknowledged Date',
  ERCompletionDate: 'ER Completion Date',
  RFQSummaryNumber: "RFQ Summary No.",
  RFQSummaryStatus: "RFQ Summary status",
  AwardedVendorName: "Awarded Vendor",
  ReasonName: "Abort / no PO Reason",
  PODocumentNumber: 'PO No.',
  POItemNumber: 'PO Item No.',
  POSubmittedDate: 'PO Submission Date',
  POProcessStatus: 'PO Status',
  POApprovedDate: 'PO Approval Date',
  POSMLNumber: 'SML No.',
};

interface FiltersForm {
  csvHeaderPreset: string;
  csvHeaderKeys: boolean[];
  MPDocumentNumber: string;
  purchaseExecutives: string[];
  requestingOfficer: string[];
  datePreset: DatePreset;
  startDate: Moment | null;
  endDate: Moment | null;
}

enum DatePreset {
  CUSTOM = 'CUSTOM',
  // PAST_30_DAYS = 'PAST_30_DAYS',
  // PAST_60_DAYS = 'PAST_60_DAYS',
  // PAST_90_DAYS = 'PAST_90_DAYS',
}

enum CsvHeaderPreset {
  ALL = 'ALL',
  CUSTOM = 'CUSTOM',
}

enum ValidationError {
  INVALID_MP_DOCUMENT_NUMBER = 'INVALID_MP_DOCUMENT_NUMBER',
  CUSTOM_MISSING_START_END_DATE = 'CUSTOM_MISSING_START_END_DATE',
  MINIMUM_ONE_CSV_HEADER = 'MINIMUM_ONE_CSV_HEADER',
  MAXIMUM_6_MONTHS = 'MAXIMUM_6_MONTHS',
}

@Component({
  selector: 'app-reports-page',
  templateUrl: './reports-page.component.html',
  styleUrls: ['./reports-page.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
  ],
})
export class ReportsPageComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();
  activePage: string = 'Purchasing Executive Report';
  userDropdown: DropDownOptions[] = [];
  peDropdown: DropDownOptions[] = [];
  private fetchedPurchaseExecutivesList = new BehaviorSubject<
    PurchaseExecutiveUser[] | null
  >(null);
  public purchaseExecutivesList$ =
    this.fetchedPurchaseExecutivesList.asObservable();
  isLoading: boolean = false;
  downloadErrorMessage: string = '';
  private uniqueFiltersStorageKey: string = 'mypurchase-ReportsPage-filters';
  private csvFileNamePrefix: string = 'MyPurchase_PE-report_';
  filtersForm = new FormGroup({
    csvHeaderPreset: new FormControl<string>(CsvHeaderPreset.CUSTOM, {
      nonNullable: true,
    }),
    csvHeaderKeys: new FormArray<FormControl<boolean>>([]),
    MPDocumentNumber: new FormControl<string>('', { nonNullable: true }),
    purchaseExecutives: new FormControl<string[]>([], { nonNullable: true }),
    requestingOfficer: new FormControl<string[]>([], { nonNullable: true }),
    datePreset: new FormControl<DatePreset>(DatePreset.CUSTOM, {
      nonNullable: true,
    }),
    startDate: new FormControl<Moment | null>(moment().subtract(183, 'days')),
    endDate: new FormControl<Moment | null>(moment()),
  });
  isEveryPurchaseExecutiveChecked: boolean = false;
  isEveryRequestingOfficerChecked: boolean = false;
  defaultPurchaseExecutivesDropdownPlaceholder = 'Filter by PE name';
  defaultRequestingOfficerPlaceholder = 'Filter by RO name';
  selectedPurchaseExecutivesLabel: string =
    this.defaultPurchaseExecutivesDropdownPlaceholder;
  selectedRequestingOfficerLabel: string =
    this.defaultRequestingOfficerPlaceholder;

  constructor(
    private BackendApi: BackendApiService,
    private store: Store<{
      dp: StoreDropdowns;
    }>
  ) {
    // Setup form array with number of form control = number of array element
    for (const key of INITIAL_CSV_HEADER_KEYS) {
      (this.filtersForm.get('csvHeaderKeys') as FormArray).push(
        new FormControl(true)
      );
    }

    // Restore filters stored in persistent browser storage
    this.restoreStoredFilters();

    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        if (dp.UserList.length) {
          this.userDropdown = dp.UserList.map((user) => ({
            ...user,
            id: user.id?.toLowerCase(),
            value: user.value,
            groupBy: 'Select All',
          }));
          setTimeout(() => {
            // Set default selected values
            this.filtersForm.get('requestingOfficer')!.setValue(this.userDropdown.map(item => item.id));
            this.updateSelectedRequestingOfficerLabel();
          }, 1000);
        }
        this.removeUnavailableSelectedUsers();
        this.everyRequestingOfficerIsChecked();
      });

    this.purchaseExecutivesList$
      .pipe(takeUntil(this.destroy$))
      .subscribe((peList) => {
        if (peList) {
          this.peDropdown = peList.map((pe) => ({
            id: pe.Username?.toLowerCase(),
            value: pe.DisplayName,
            groupBy: 'Select All',
          }));
          this.filtersForm.get('purchaseExecutives')!.setValue(this.peDropdown.map(item => item.id));
        }
        this.removeUnavailableSelectedUsers();
        this.everyPurchaseExecutiveIsChecked();
        this.updateSelectedPurchaseExecutivesLabel();
      });
  }

  async ngOnInit(): Promise<void> {
    // When form value changes, store values in persistent browser storage
    this.filtersForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((values) => {
        this.storeFilters();

        this.everyPurchaseExecutiveIsChecked();
        this.everyRequestingOfficerIsChecked();
        this.updateSelectedPurchaseExecutivesLabel();
        this.updateSelectedRequestingOfficerLabel();
      });
    // Subscribe to value changes of the FormArray
    this.csvHeaderKeys.valueChanges.subscribe(() => {
      // Recalculate select all and partial selection states
    });

    // Get purchase executive dropdown list
    await this.fetchPurchaseExecutivesList();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get csvHeaderKeys(): FormArray {
    return this.filtersForm.get('csvHeaderKeys') as FormArray;
  }

  removeUnavailableSelectedUsers() {
    const selectedPEs = this.filtersForm.get('purchaseExecutives')?.value;
    const selectedRO = this.filtersForm.get('requestingOfficer')?.value;

    if (
      Array.isArray(selectedPEs) &&
      Array.isArray(this.peDropdown) &&
      this.peDropdown.length > 0
    ) {
      const uniquePEs = new Set(this.peDropdown.map((pe) => pe.id));
      const availableSelectedPEs = selectedPEs.filter((pe) =>
        uniquePEs.has(pe)
      );

      if (selectedPEs.length !== availableSelectedPEs.length) {
        this.filtersForm
          .get('purchaseExecutives')
          ?.setValue(availableSelectedPEs);
      }
    }

    if (
      Array.isArray(selectedRO) &&
      Array.isArray(this.userDropdown) &&
      this.userDropdown.length > 0
    ) {
      const uniqueROs = new Set(this.userDropdown.map((user) => user.value));
      const availableSelectedRO = selectedRO.filter((ro) =>
        uniqueROs.has(ro)
      );
      if (selectedRO.length !== availableSelectedRO.length) {
        this.filtersForm
          .get('requestingOfficer')
          ?.setValue(availableSelectedRO);
      }
    }
  }

  async breadcrumbsButtonActionHandler($event: any) {
    switch ($event.actionType as string) {
      case 'download-report':
        this.downloadPurchaseExecutivesReport();
        break;
      case 'reset-filter':
        this.resetFilter();
        break;
      default:
        // do nothing
        break;
    }
  }

  async fetchPurchaseExecutivesList() {
    this.isLoading = true;
    try {
      const response: PurchaseExecutiveListResponse = await lastValueFrom(
        this.BackendApi.getPurchaseExecutives()
      );

      if (!response || !Array.isArray(response)) {
        throw new Error('Empty response or not array of PEs');
      }

      this.fetchedPurchaseExecutivesList.next(response);
    } catch (err: any) {
      console.log(
        'ReportsPage fetchPurchaseExecutivesList failed due to error',
        err
      );
    } finally {
      this.isLoading = false;
    }
  }

  restoreStoredFilters() {
    // Restore any stored filters selected previously
    const storedFilters = localStorage.getItem(this.uniqueFiltersStorageKey);

    if (!storedFilters) {
      return;
    }

    const storedFiltersObj = JSON.parse(storedFilters);

    for (const key of Object.keys(storedFiltersObj)) {
      const value = storedFiltersObj[key];
      if (!value || !(key in this.filtersForm.value)) {
        // Only overwrite default filters if there is stored value
        delete storedFiltersObj[key];
      } else if (key === 'csvHeaderKeys') {
        // If there are changes to this functionality in the future and header keys change, remove obsolete stored header keys
        if (value.length !== this.csvHeaderKeys.value.length) {
          delete storedFiltersObj[key];
        }
      } else if (key === 'startDate' || key === 'endDate') {
        storedFiltersObj[key] = moment(value);
      }
    }

    let initialFilters: FiltersForm = {
      ...this.filtersForm.value,
      ...storedFiltersObj,
    };

    this.filtersForm.setValue(initialFilters);
  }

  updateSelectedPurchaseExecutivesLabel() {
    const selectedPEUsernames =
      this.filtersForm.get('purchaseExecutives')?.value;
    if (
      !Array.isArray(this.peDropdown) ||
      this.peDropdown.length === 0 ||
      !selectedPEUsernames ||
      selectedPEUsernames.length === 0
    ) {
      this.selectedPurchaseExecutivesLabel =
        this.defaultPurchaseExecutivesDropdownPlaceholder;
      return;
    }
    if (this.isEveryPurchaseExecutiveChecked) {
      this.selectedPurchaseExecutivesLabel = 'All';
      return;
    }

    let label = this.peDropdown
      .filter((pe) => selectedPEUsernames.includes(pe.id))
      .map((pe) => pe.value)
      .join(', ');
    if (!label) {
      label = this.defaultPurchaseExecutivesDropdownPlaceholder;
    }
    this.selectedPurchaseExecutivesLabel = label;
  }

  updateSelectedRequestingOfficerLabel() {
    const selectedROUsernames =
      this.filtersForm.get('requestingOfficer')?.value;
    if (
      !Array.isArray(this.userDropdown) ||
      this.userDropdown.length === 0 ||
      !selectedROUsernames ||
      selectedROUsernames.length === 0
    ) {
      this.selectedRequestingOfficerLabel =
        this.defaultRequestingOfficerPlaceholder;
      return;
    }
    if (this.isEveryRequestingOfficerChecked) {
      this.selectedRequestingOfficerLabel = 'All';
      return;
    }

    let label = this.userDropdown
      .filter((ro) => selectedROUsernames.includes(ro.id))
      .map((ro) => ro.value)
      .join(', ');
    if (!label) {
      label = this.defaultRequestingOfficerPlaceholder;
    }

    this.selectedRequestingOfficerLabel = label;
  }

  storeFilters() {
    const storedFilters =
      localStorage.getItem(this.uniqueFiltersStorageKey) ?? '{}';
    const storedFiltersObj: FiltersForm = JSON.parse(storedFilters);

    const newFiltersObj = { ...storedFiltersObj, ...this.filtersForm.value };
    const newFiltersString = JSON.stringify(newFiltersObj);
    localStorage.setItem(this.uniqueFiltersStorageKey, newFiltersString);
  }

  resetFilter() {
    // event.stopPropagation();
    this.filtersForm.reset({
      MPDocumentNumber: '',
      purchaseExecutives: this.peDropdown?.map(item => item.id),
      requestingOfficer: this.userDropdown?.map(item => item.id),
      startDate: moment().subtract(183, 'days'),
      endDate: moment(),
      csvHeaderPreset: CsvHeaderPreset.CUSTOM
    });
    this.csvHeaderKeys.controls.forEach((control) => {
      control.setValue(true);
    });
  }

  // resetCsvHeaders(event: MouseEvent) {
  //   event.stopPropagation();
  //   this.filtersForm.get('csvHeaderPreset')?.reset();
  // }

  validateFilters(filters: Partial<FiltersForm>) {
    // Remove any existing form error
    Object.keys(this.filtersForm.controls).forEach((key) => {
      const control = this.filtersForm.get(key);
      // Clear errors on individual form controls
      control?.setErrors(null);
    });

    const {
      MPDocumentNumber,
      datePreset,
      startDate,
      endDate,
      csvHeaderPreset,
      csvHeaderKeys,
    } = filters;

    // Typed MPDocumentNumber filter value, but invalid format
    if (MPDocumentNumber && !isMPDocumentNumberValid(MPDocumentNumber)) {
      this.filtersForm
        .get('MPDocumentNumber')
        ?.setErrors({ [ValidationError.INVALID_MP_DOCUMENT_NUMBER]: true });
    }

    // Selected custom date range, but start or end date is missing
    if (datePreset === DatePreset.CUSTOM && (!startDate || !endDate)) {
      this.filtersForm
        .get('startDate')
        ?.setErrors({ [ValidationError.CUSTOM_MISSING_START_END_DATE]: true });
    }

    // Selected custom date range and span is more than 6 months
    else if (
      datePreset === DatePreset.CUSTOM &&
      endDate &&
      startDate &&
      endDate.diff(startDate, 'months', true) > 6
    ) {
      this.filtersForm
        .get('startDate')
        ?.setErrors({ [ValidationError.MAXIMUM_6_MONTHS]: true });
    }

    // Selected custom 'column to display' preset, but all header column unchecked
    if (
      csvHeaderPreset === CsvHeaderPreset.CUSTOM &&
      csvHeaderKeys?.every((key) => !key)
    ) {
      this.filtersForm
        .get('csvHeaderPreset')
        ?.setErrors({ [ValidationError.MINIMUM_ONE_CSV_HEADER]: true });
    }

    return { isSuccess: !this.filtersForm.invalid };
  }

  async downloadPurchaseExecutivesReport() {
    this.downloadErrorMessage = '';

    const formValue = this.filtersForm.value;
    const { isSuccess } = this.validateFilters(formValue);

    if (!isSuccess) {
      return;
    }

    const todayDate = moment().toDate();
    const lastMonthDate = moment()
      .subtract('30', 'days')
      .startOf('day')
      .toDate();

    // Format request body
    let reqbody: PurchaseExecutivesReportRequest['filters'] = {
      MPDocumentNumber: formValue.MPDocumentNumber
        ? formValue.MPDocumentNumber
        : null,
      purchaseExecutives:
        formValue.purchaseExecutives && formValue.purchaseExecutives.length > 0
          ? formValue.purchaseExecutives
          : null,
      requestingOfficer: formValue.requestingOfficer && formValue.requestingOfficer.length > 0
        ? formValue.requestingOfficer
        : null,
      startDate:
        formValue.datePreset === DatePreset.CUSTOM
          ? formValue.startDate?.toDate() ?? lastMonthDate
          : lastMonthDate,
      endDate:
        formValue.datePreset === DatePreset.CUSTOM
          ? formValue.endDate?.toDate() ?? todayDate
          : todayDate,
    };

    // if (formValue.datePreset !== DatePreset.CUSTOM) {
    //   const splitDatePreset = formValue.datePreset?.split('_') ?? [];

    //   if (splitDatePreset[0] === 'PAST' && splitDatePreset[2] === 'DAYS') {
    //     const days = Number(splitDatePreset[1]);
    //     reqbody.startDate = moment()
    //       .subtract(days + 1, 'days')
    //       .startOf('day')
    //       .toDate();
    //     reqbody.endDate = todayDate;
    //   }
    // }

    this.isLoading = true;

    try {
      let reportData: PurchaseExecutiveReportRow[] = await lastValueFrom(
        this.BackendApi.getPurchaseExecutivesReport(reqbody).pipe(
          takeUntil(this.destroy$)
        )
      );

      if (Array.isArray(reportData)) {
        this.formatAndSaveReport(reportData);
      }
    } catch (err) {
      console.log(
        'Error encountered while downloading purchase executives report',
        err
      );
      this.downloadErrorMessage =
        'Something went wrong during the report download, please try again later.';
    } finally {
      this.isLoading = false;
    }
  }

  private everyPurchaseExecutiveIsChecked() {
    if (this.peDropdown.length === 0) {
      this.isEveryPurchaseExecutiveChecked = false;
      return;
    }
    this.isEveryPurchaseExecutiveChecked = this.peDropdown.every((option) =>
      this.filtersForm.get('purchaseExecutives')!.value.includes(option.id)
    );
  }

  private everyRequestingOfficerIsChecked() {
    if (this.userDropdown.length === 0) {
      this.isEveryRequestingOfficerChecked = false;
      return;
    }
    this.isEveryRequestingOfficerChecked = this.userDropdown.every((option) =>
      this.filtersForm.get('requestingOfficer')!.value.includes(option.id)
    );
  }

  isSelected(id: string): boolean {
    return this.filtersForm.get('purchaseExecutives')!.value.includes(id);
  }

  isSelectedRo(id: string): boolean {
    return this.filtersForm.get('requestingOfficer')!.value.includes(id);
  }

  isSelectAllColumnSelected(): boolean {
    return this.csvHeaderKeys.controls.every((control) => control.value);
  }

  isPartialSelection(): boolean {
    const selectedCount = this.csvHeaderKeys.controls.filter((control) => control.value).length;
    return selectedCount > 0 && selectedCount < this.csvHeaderKeys.length;
  }

  toggleAllDisplayColumn(event: any): void {
    const checked = event.checked;
    this.csvHeaderKeys.controls.forEach((control) => control.setValue(checked));
  }

  onIndividualCheckboxChange(): void {
    console.log('Checkbox state changed:', this.csvHeaderKeys.value);
  }

  private formatAndSaveReport(reportData: PurchaseExecutiveReportRow[]) {
    let selectedHeaderKeys: CsvHeaderKey[] = INITIAL_CSV_HEADER_KEYS.map(
      (key) => key
    );

    if (this.filtersForm.value.csvHeaderPreset === CsvHeaderPreset.CUSTOM) {
      const formCsvHeaderKeys: FiltersForm['csvHeaderKeys'] =
        this.csvHeaderKeys.value;
      selectedHeaderKeys = INITIAL_CSV_HEADER_KEYS.filter(
        (key, index) => formCsvHeaderKeys[index]
      );
    }

    const csv = Papa.unparse({
      fields: selectedHeaderKeys.map((key) => CSV_HEADER_MAPPING[key]),
      data: reportData.map((rowObj) => {
        return selectedHeaderKeys.map((key) => {
          if (rowObj[key] && key == 'PurchaseExecutiveName')
            return rowObj[key]?.toString().replaceAll('\r\n', '');
          else return rowObj[key] ?? null;
        });
      }),
    });

    // Download csv file
    var blob = new Blob([csv]);
    var a = window.document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = `${this.csvFileNamePrefix}${moment().format(
      'YYYYMMDDHHmmss'
    )}.csv`;
    document.body.appendChild(a);
    a.click(); // IE: "Access is denied"; see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
    document.body.removeChild(a);
  }

  public get alertType(): typeof AlertType {
    return AlertType;
  }
  public get DatePreset(): typeof DatePreset {
    return DatePreset;
  }
  public get CsvHeaderPreset(): typeof CsvHeaderPreset {
    return CsvHeaderPreset;
  }
  public get ValidationError(): typeof ValidationError {
    return ValidationError;
  }

  public get INITIAL_CSV_HEADER_KEYS(): typeof INITIAL_CSV_HEADER_KEYS {
    return INITIAL_CSV_HEADER_KEYS;
  }

  public get CSV_HEADER_MAPPING(): typeof CSV_HEADER_MAPPING {
    return CSV_HEADER_MAPPING;
  }
}
